import React from "react";

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const gearPoints = (cx, cy, radius, teethValue) => {
  const teeth = Math.max(8, Math.min(48, Math.round(number(teethValue, 20))));
  return Array.from({ length: teeth * 2 }, (_, index) => {
    const angle = (index / (teeth * 2)) * Math.PI * 2;
    const r = index % 2 === 0 ? radius : radius * 0.88;
    return `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`;
  }).join(" ");
};

const GearDiagram = ({ gears }) => {
  const safeGears = (gears || []).slice(0, 6);
  const maxTeeth = Math.max(...safeGears.map((gear) => number(gear.teeth, 20)), 20);
  return (
    <svg viewBox="0 0 720 220" role="img" aria-label="Gear relationship diagram">
      <line x1="45" y1="110" x2="675" y2="110" stroke="rgba(148,163,184,.25)" strokeWidth="2" />
      {safeGears.map((gear, index) => {
        const radius = 34 + (number(gear.teeth, 20) / maxTeeth) * 34;
        const cx = safeGears.length === 1 ? 360 : 75 + (index * 570) / Math.max(1, safeGears.length - 1);
        return (
          <g key={gear.id || index}>
            <polygon points={gearPoints(cx, 110, radius, gear.teeth)} fill="rgba(124,92,255,.18)" stroke="#8b5cf6" strokeWidth="2" />
            <circle cx={cx} cy="110" r="10" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
            <text x={cx} y="114" fill="#f8fafc" fontSize="12" fontWeight="800" textAnchor="middle">{number(gear.teeth, 0)}T</text>
            <text x={cx} y="205" fill="#94a3b8" fontSize="11" textAnchor="middle">{gear.shaftId || `Shaft ${index + 1}`}</text>
          </g>
        );
      })}
    </svg>
  );
};

const FlatPatternDiagram = ({ params }) => {
  const legA = number(params.legA || params.lengthA, 3);
  const legB = number(params.legB || params.lengthB, 2);
  const split = 40 + (legA / Math.max(legA + legB, 0.001)) * 500;
  return (
    <svg viewBox="0 0 620 190" role="img" aria-label="Flat pattern leg and bend diagram">
      <rect x="40" y="72" width="540" height="46" rx="5" fill="rgba(34,211,238,.1)" stroke="#22d3ee" strokeWidth="2" />
      <rect x={split - 10} y="62" width="20" height="66" fill="rgba(249,115,22,.28)" stroke="#f97316" strokeWidth="2" />
      <line x1="40" y1="145" x2={split - 10} y2="145" stroke="#94a3b8" />
      <line x1={split + 10} y1="145" x2="580" y2="145" stroke="#94a3b8" />
      <text x={(40 + split - 10) / 2} y="165" fill="#cbd5e1" fontSize="13" textAnchor="middle">Leg A {legA}</text>
      <text x={(split + 10 + 580) / 2} y="165" fill="#cbd5e1" fontSize="13" textAnchor="middle">Leg B {legB}</text>
      <text x={split} y="48" fill="#fb923c" fontSize="12" fontWeight="800" textAnchor="middle">Bend zone</text>
    </svg>
  );
};

const ChordDiagram = ({ params }) => (
  <svg viewBox="0 0 620 220" role="img" aria-label="Chord height and radius diagram">
    <path d="M 80 165 Q 310 22 540 165" fill="none" stroke="#8b5cf6" strokeWidth="4" />
    <line x1="80" y1="165" x2="540" y2="165" stroke="#22d3ee" strokeWidth="3" />
    <line x1="310" y1="165" x2="310" y2="93" stroke="#fb923c" strokeWidth="2" strokeDasharray="6 5" />
    <circle cx="310" cy="165" r="4" fill="#f8fafc" />
    <text x="310" y="190" fill="#cbd5e1" fontSize="13" textAnchor="middle">Chord {number(params.chord, 0)}</text>
    <text x="325" y="125" fill="#fb923c" fontSize="12">Sagitta</text>
    <text x="470" y="70" fill="#a78bfa" fontSize="12">Radius {number(params.radius, 0)}</text>
  </svg>
);

const DriveDiagram = ({ toolId, params }) => {
  const isChain = toolId === "chain-sprocket-ratio";
  const left = number(params.driverTeeth || params.driverDiameter, 20);
  const right = number(params.drivenTeeth || params.drivenDiameter, 40);
  return (
    <svg viewBox="0 0 620 210" role="img" aria-label={`${isChain ? "Chain sprocket" : "Belt pulley"} diagram`}>
      <circle cx="170" cy="105" r="58" fill="rgba(34,211,238,.1)" stroke="#22d3ee" strokeWidth="3" />
      <circle cx="450" cy="105" r="78" fill="rgba(124,92,255,.13)" stroke="#8b5cf6" strokeWidth="3" />
      <path d="M 170 47 L 450 27 M 170 163 L 450 183" fill="none" stroke={isChain ? "#f59e0b" : "#94a3b8"} strokeWidth={isChain ? "5" : "8"} strokeDasharray={isChain ? "8 6" : "0"} />
      <text x="170" y="110" fill="#f8fafc" fontSize="15" fontWeight="800" textAnchor="middle">{left}</text>
      <text x="450" y="110" fill="#f8fafc" fontSize="15" fontWeight="800" textAnchor="middle">{right}</text>
      <text x="310" y="203" fill="#94a3b8" fontSize="12" textAnchor="middle">{isChain ? "Pitch chain relationship" : "Belt pitch-line relationship"}</text>
    </svg>
  );
};

const ManufacturingDiagram = ({ toolId, params = {}, gears = [] }) => {
  let diagram = null;
  if (toolId === "gear-ratio") diagram = <GearDiagram gears={gears} />;
  if (["gear-pitch-diameter", "module-gear", "diametral-pitch"].includes(toolId)) {
    diagram = <GearDiagram gears={[
      { id: "gear-a", teeth: params.teeth || 20, shaftId: "Calculated gear" },
      { id: "gear-b", teeth: Math.max(8, number(params.teeth, 20) * 1.5), shaftId: "Mating reference" },
    ]} />;
  }
  if (toolId === "flat-pattern") diagram = <FlatPatternDiagram params={params} />;
  if (["chord-height", "radius-from-chord"].includes(toolId)) diagram = <ChordDiagram params={params} />;
  if (["belt-speed", "pulley-ratio", "chain-sprocket-ratio", "belt-center-distance"].includes(toolId)) {
    diagram = <DriveDiagram toolId={toolId} params={params} />;
  }
  if (!diagram) return null;
  return (
    <section className="calculator-result-card" style={{ marginBottom: "16px", overflow: "hidden" }}>
      <span className="calculator-result-label">Process Geometry Preview</span>
      <div style={{ width: "100%", minHeight: "160px", marginTop: "8px" }}>{diagram}</div>
      <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
        Visual aid only. Dimensions and geometry are not drawing scale.
      </p>
    </section>
  );
};

export default ManufacturingDiagram;
